/**
 * Toast Notification System
 * Provides non-intrusive feedback for user actions
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import './Toast.css';

// Toast context
const ToastContext = createContext();

// Maximum number of visible toasts at once
const MAX_VISIBLE_TOASTS = 3;

// Deduplication window in milliseconds — identical messages within this window are suppressed
const DEDUP_WINDOW_MS = 5000;

// Toast types with icons
const TOAST_TYPES = {
  success: {
    icon: '✓',
    className: 'toast-success',
    defaultDuration: 4000,
  },
  error: {
    icon: '✕',
    className: 'toast-error',
    defaultDuration: 6000,
  },
  warning: {
    icon: '⚠',
    className: 'toast-warning',
    defaultDuration: 5000,
  },
  info: {
    icon: 'ℹ',
    className: 'toast-info',
    defaultDuration: 4000,
  },
  loading: {
    icon: '◌',
    className: 'toast-loading',
    defaultDuration: null, // Must be dismissed manually
  },
};

/**
 * Individual Toast Component
 */
const Toast = ({ id, type, title, message, action, onDismiss, progress }) => {
  const config = TOAST_TYPES[type] || TOAST_TYPES.info;

  return (
    <div
      className={`toast ${config.className}`}
      role="alert"
      aria-live={type === 'error' ? 'assertive' : 'polite'}
    >
      <div className="toast-icon" aria-hidden="true">
        {type === 'loading' ? (
          <span className="toast-spinner">{config.icon}</span>
        ) : (
          config.icon
        )}
      </div>
      <div className="toast-content">
        {title && <div className="toast-title">{title}</div>}
        <div className="toast-message">{message}</div>
        {action && (
          <button
            className="toast-action"
            onClick={() => {
              action.onClick();
              onDismiss(id);
            }}
          >
            {action.label}
          </button>
        )}
      </div>
      {type !== 'loading' && (
        <button
          className="toast-dismiss"
          onClick={() => onDismiss(id)}
          aria-label="Dismiss notification"
        >
          ×
        </button>
      )}
      {progress !== undefined && (
        <div className="toast-progress" style={{ width: `${progress}%` }} />
      )}
    </div>
  );
};

/**
 * Toast Container - renders all active toasts
 */
const ToastContainer = ({ toasts, onDismiss }) => {
  if (toasts.length === 0) return null;

  return (
    <div className="toast-container" aria-label="Notifications">
      {toasts.map((toast) => (
        <Toast key={toast.id} {...toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
};

/**
 * Toast Provider - manages toast state and provides context
 */
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const recentMessagesRef = useRef(new Map());

  // Auto-dismiss toasts
  useEffect(() => {
    const timers = toasts
      .filter(toast => toast.duration !== null)
      .map(toast => {
        return setTimeout(() => {
          dismiss(toast.id);
        }, toast.duration);
      });

    return () => timers.forEach(clearTimeout);
  }, [toasts]);

  // Add a new toast
  const show = useCallback((options) => {
    // Deduplication: suppress identical messages within the dedup window
    const dedupKey = `${options.type || 'info'}:${options.message}`;
    const now = Date.now();
    const lastShown = recentMessagesRef.current.get(dedupKey);
    if (lastShown && now - lastShown < DEDUP_WINDOW_MS) {
      return null; // Suppress duplicate
    }
    recentMessagesRef.current.set(dedupKey, now);

    // Clean up old dedup entries
    for (const [key, timestamp] of recentMessagesRef.current) {
      if (now - timestamp > DEDUP_WINDOW_MS) {
        recentMessagesRef.current.delete(key);
      }
    }

    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const config = TOAST_TYPES[options.type] || TOAST_TYPES.info;

    const toast = {
      id,
      type: options.type || 'info',
      title: options.title,
      message: options.message,
      action: options.action,
      duration: options.duration !== undefined ? options.duration : config.defaultDuration,
      progress: options.progress,
    };

    setToasts(prev => {
      const updated = [...prev, toast];
      // Enforce max visible toasts — remove oldest non-loading toasts
      if (updated.length > MAX_VISIBLE_TOASTS) {
        const excess = updated.length - MAX_VISIBLE_TOASTS;
        let removed = 0;
        return updated.filter(t => {
          if (removed >= excess) return true;
          if (t.type !== 'loading' && t.id !== id) {
            removed++;
            return false;
          }
          return true;
        });
      }
      return updated;
    });
    return id;
  }, []);

  // Dismiss a toast by ID
  const dismiss = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  // Update a toast (useful for loading -> success transitions)
  const update = useCallback((id, options) => {
    setToasts(prev => prev.map(toast => {
      if (toast.id === id) {
        const config = TOAST_TYPES[options.type] || TOAST_TYPES[toast.type];
        return {
          ...toast,
          ...options,
          duration: options.duration !== undefined ? options.duration : config.defaultDuration,
        };
      }
      return toast;
    }));
  }, []);

  // Convenience methods
  const success = useCallback((message, options = {}) => {
    return show({ type: 'success', message, ...options });
  }, [show]);

  const error = useCallback((message, options = {}) => {
    return show({ type: 'error', message, ...options });
  }, [show]);

  const warning = useCallback((message, options = {}) => {
    return show({ type: 'warning', message, ...options });
  }, [show]);

  const info = useCallback((message, options = {}) => {
    return show({ type: 'info', message, ...options });
  }, [show]);

  const loading = useCallback((message, options = {}) => {
    return show({ type: 'loading', message, ...options });
  }, [show]);

  // Promise-based toast (shows loading, then success/error)
  const promise = useCallback(async (promiseFn, options = {}) => {
    const id = loading(options.loading || 'Loading...');

    try {
      const result = await promiseFn;
      update(id, {
        type: 'success',
        message: options.success || 'Success!',
      });
      return result;
    } catch (err) {
      update(id, {
        type: 'error',
        message: options.error || err.message || 'Something went wrong',
      });
      throw err;
    }
  }, [loading, update]);

  // Stable across renders: a new object every time a toast appeared made every
  // useCallback/useEffect that lists `toast` re-run (refetching pages — the
  // course player went blank right after "Enrolled successfully!").
  const value = useMemo(() => ({
    show,
    dismiss,
    update,
    success,
    error,
    warning,
    info,
    loading,
    promise,
  }), [show, dismiss, update, success, error, warning, info, loading, promise]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
};

/**
 * Hook to use toast notifications
 */
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export default ToastProvider;
